import {
  ALIGN_TYPES,
  CONTENT_MARGIN,
  DEFAULT_CONTENT_HEIGHT_WITHOUT_PADDING,
  ICON_OFFSET,
  MAX_CONTENT_WIDTH,
  OVERFLOW_SIDE,
  PAGE_PADDING,
  PLACEMENT,
} from './constants';
import getOverflowSides from './getOverflowSides';
import { Placement } from './types';

type CalculateParams = {
  contentRect: DOMRect;
  triggerRect: DOMRect;
  scrollContainerRect: DOMRect | null;
  alignType: typeof ALIGN_TYPES[keyof typeof ALIGN_TYPES];
  currentPlacement: Placement;
  shouldChangePlacementOnOverflowOnly: boolean;
  shouldLimitHeightByScreen: boolean;
  isVerticalAutoPlacementDisabled: boolean;
  scrollContainer: HTMLElement;
};

export type PlacementStyles = {
  left: number;
  top: number;
  maxHeight: number;
  width: number;
};

export function calculatePlacementAndStyles({
  contentRect,
  triggerRect,
  scrollContainerRect,
  alignType,
  currentPlacement,
  shouldChangePlacementOnOverflowOnly,
  shouldLimitHeightByScreen,
  isVerticalAutoPlacementDisabled,
  scrollContainer,
}: CalculateParams) {
  const {
    bottom: triggerBottom,
    left: triggerLeft,
    right: triggerRight,
    top: triggerTop,
    width: triggerWidth,
  } = triggerRect;

  const { top: containerTop = 0, left: containerLeft = 0 } = scrollContainerRect || {};

  const { width: contentWidth, height: contentHeight } = contentRect;

  const placementStyles: PlacementStyles = {
    left: 0,
    width: 0,
    maxHeight: DEFAULT_CONTENT_HEIGHT_WITHOUT_PADDING,
    top: triggerBottom + CONTENT_MARGIN + scrollContainer.scrollTop - containerTop,
  };

  let realPlacement: typeof PLACEMENT[keyof typeof PLACEMENT] = PLACEMENT.BOTTOM;

  const minWidth = triggerWidth + ICON_OFFSET;
  const relativeWidth = contentWidth > minWidth ? contentWidth : minWidth;
  const hasTooBigWidth = contentWidth > MAX_CONTENT_WIDTH && minWidth < MAX_CONTENT_WIDTH;

  switch (alignType) {
    case ALIGN_TYPES.TRIGGER_WIDTH:
      placementStyles.width = triggerWidth;
      placementStyles.left = triggerLeft - containerLeft;
      break;
    case ALIGN_TYPES.LEFT:
      placementStyles.width = hasTooBigWidth ? MAX_CONTENT_WIDTH : relativeWidth;
      placementStyles.left = triggerLeft - containerLeft;
      break;
    case ALIGN_TYPES.RIGHT:
      placementStyles.width = hasTooBigWidth ? MAX_CONTENT_WIDTH : relativeWidth;
      placementStyles.left = triggerRight - placementStyles.width - containerLeft;
      break;
  }

  if (shouldLimitHeightByScreen) {
    const diff =
      placementStyles.top + contentHeight - (scrollContainer.scrollTop + window.innerHeight);

    placementStyles.maxHeight = contentHeight - diff - PAGE_PADDING;

    return {
      placementStyles,
      realPlacement,
    };
  }

  // проверяет вылезает ли контент за рамки экрана (+ опр офсет)
  const overflowSides = getOverflowSides(
    contentWidth,
    contentHeight,
    PAGE_PADDING,
    placementStyles
  );

  const overFlowBottom = overflowSides.includes(OVERFLOW_SIDE.BOTTOM);

  if (overflowSides.includes(OVERFLOW_SIDE.LEFT)) {
    placementStyles.left = PAGE_PADDING;
    placementStyles.width = triggerRect.left + triggerWidth - placementStyles.left;
  }

  if (overflowSides.includes(OVERFLOW_SIDE.RIGHT)) {
    placementStyles.width = window.innerWidth - placementStyles.left - PAGE_PADDING;
  }

  if (shouldChangePlacementOnOverflowOnly && currentPlacement === PLACEMENT.BOTTOM) {
    return {
      placementStyles,
      realPlacement,
    };
  }

  if (isVerticalAutoPlacementDisabled) {
    return {
      placementStyles,
      realPlacement,
    };
  }

  // correct placement if some content parts are outside the screen
  if (
    overFlowBottom ||
    (shouldChangePlacementOnOverflowOnly && currentPlacement === PLACEMENT.TOP)
  ) {
    realPlacement = PLACEMENT.TOP;
    placementStyles.top = triggerTop - CONTENT_MARGIN - contentHeight + scrollContainer.scrollTop; // changes vertical position to "top" equivalent
  }

  return {
    placementStyles,
    realPlacement,
  };
}
